import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const icons = {
  success: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />,
  error: <XCircle className="w-5 h-5 text-red-500 shrink-0" />,
  warning: <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />,
  info: <Info className="w-5 h-5 text-blue-500 shrink-0" />,
};

const bgColors = {
  success: 'bg-emerald-50/95 border-emerald-200/60 text-emerald-900 shadow-emerald-100/30',
  error: 'bg-red-50/95 border-red-200/60 text-red-900 shadow-red-100/30',
  warning: 'bg-amber-50/95 border-amber-200/60 text-amber-900 shadow-amber-100/30',
  info: 'bg-blue-50/95 border-blue-200/60 text-blue-900 shadow-blue-100/30',
};

const progressColors = {
  success: 'bg-emerald-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
};

const ToastItem = ({ toast, removeToast }) => {
  const { id, message, type, duration } = toast;

  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, removeToast]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -15, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
      className={`pointer-events-auto relative flex items-start gap-3 p-4 border rounded-xl shadow-xl backdrop-blur-md overflow-hidden w-full sm:w-[360px] ${bgColors[type]}`}
    >
      {icons[type]}
      <div className="flex-1 text-sm font-medium pr-4 leading-snug">
        {message}
      </div>
      <button
        onClick={() => removeToast(id)}
        className="text-gray-400 hover:text-gray-600 transition-colors shrink-0 p-0.5 rounded-full hover:bg-black/5"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress Bar indicating time left */}
      <motion.div
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: duration / 1000, ease: 'linear' }}
        className={`absolute bottom-0 left-0 h-1 ${progressColors[type]}`}
      />
    </motion.div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-24 right-4 left-4 sm:left-auto sm:right-5 z-[9999] flex flex-col gap-3 pointer-events-none w-auto max-w-[calc(100vw-32px)] sm:max-w-sm sm:w-full">
      <AnimatePresence mode="pop-layout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
